<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
    />



    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ pageTitle }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ pageSubtitle }}
              </p>
            </div>
          </div>

          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Tabs -->
            <header
              class="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100 dark:border-slate-800"
            >
              <div
                class="inline-flex items-center gap-2 p-1 rounded-full bg-slate-50 dark:bg-slate-800"
              >
                <button
                  type="button"
                  @click="activeTab = 'account'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition shadow-sm"
                  :class="
                    activeTab === 'account'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'account'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-account-outline"></i>
                  </span>
                  <span>{{ t("account_tab_account") }}</span>
                </button>

                <button
                  type="button"
                  @click="activeTab = 'security'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition"
                  :class="
                    activeTab === 'security'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'security'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-lock-outline"></i>
                  </span>
                  <span>{{ t("account_tab_security") }}</span>
                </button>
              </div>

              <span class="hidden text-xs text-slate-400 sm:inline">
                {{ t("account_last_updated_hint") }}
              </span>
            </header>

            <!-- ACCOUNT -->
            <form
              v-if="activeTab === 'account'"
              class="space-y-6"
              @submit.prevent="saveSettings"
            >
              <input type="hidden" v-model="form.consultant.specialization" />
              <input type="hidden" v-model="form.consultant.qualification" />

              <!-- Consultant section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-briefcase-outline text-secondary"></i>
                  <span>
                    {{
                      isOffice
                        ? t("office_title")
                        : t("consultant_profile_title")
                    }}
                  </span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div
                    class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                  >
                    <!-- ✅ Profile Image -->
                    <div class="md:col-span-2 lg:col-span-3">
                      <label
                        class="block mb-2 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consultant_field_image") }}
                      </label>

                      <div
                        class="flex flex-col gap-4 p-4 border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-2xl sm:flex-row sm:items-center"
                      >
                        <!-- Preview -->
                        <div class="flex items-center gap-3">
                          <div
                            class="relative w-16 h-16 overflow-hidden border rounded-2xl bg-white/70 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                          >
                            <img
                              v-if="form.consultant.logo_url"
                              :src="form.consultant.logo_url"
                              alt="profile"
                              class="object-cover w-full h-full"
                            />
                            <div
                              v-else
                              class="flex items-center justify-center w-full h-full text-slate-400"
                            >
                              <i
                                class="mdi mdi-account-circle-outline text-[34px]"
                              ></i>
                            </div>

                            <span
                              class="absolute flex items-center justify-center w-6 h-6 text-white rounded-full -bottom-2 -right-2 bg-primary"
                            >
                              <i class="mdi mdi-camera-outline text-[14px]"></i>
                            </span>
                          </div>

                          <div class="min-w-0">
                            <p
                              class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                            >
                              {{ t("consultant_field_image") }}
                            </p>
                            <p
                              class="text-xs text-slate-500 dark:text-slate-400"
                            >
                              {{ t("upload_image_hint") }}
                            </p>

                            <p
                              class="mt-1 text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[220px]"
                            >
                              {{
                                form.consultant.image_meta?.name
                                  ? form.consultant.image_meta.name
                                  : t("no_file_chosen")
                              }}
                            </p>
                          </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex flex-wrap gap-2 sm:ms-auto">
                          <label
                            class="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-white shadow-sm cursor-pointer rounded-xl bg-primary hover:bg-primary/90"
                          >
                            <i class="mdi mdi-upload"></i>
                            {{ t("choose_file") }}
                            <input
                              type="file"
                              accept="image/*"
                              class="hidden"
                              @change="onImageChange"
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    <!-- Office Name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("office_name") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-office-building-outline text-[18px]"
                          ></i>
                        </span>

                        <input
                          v-model="form.consultant.office_name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('office_name_ph')"
                        />

                        <p
                          v-if="errors?.consultant?.office_name"
                          class="mt-1 text-xs text-red-500"
                        >
                          {{ errors.consultant.office_name[0] }}
                        </p>
                      </div>
                    </div>

                    <!-- Name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consultant_field_name") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.consultant.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('consultant_name_ph')"
                        />

                        <p
                          v-if="errors?.consultant?.name"
                          class="mt-1 text-xs text-red-500"
                        >
                          {{ errors.consultant.name[0] }}
                        </p>
                      </div>
                    </div>

                    <!-- License number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consultant_field_license_number") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-card-account-details-outline text-[18px]"
                          ></i>
                        </span>

                        <input
                          v-model="form.consultant.license_number"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('license_no_ph')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- ✅ Address Section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-map-marker-outline text-secondary"></i>
                  <span>{{ t("consultant_section_address") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- City -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_city") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-city-variant-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.city"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_city_ph')"
                        />
                      </div>
                    </div>

                    <!-- District -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_district") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-map-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.district"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_district_ph')"
                        />
                      </div>
                    </div>

                    <!-- Street -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_street") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-sign-direction text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.street"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_street_ph')"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Building -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_building") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-office-building-marker-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.building"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_building_number_ph')"
                        />
                      </div>
                    </div>

                    <!-- Additional -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_additional") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pound text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.additional"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_additional_number_ph')"
                        />
                      </div>
                    </div>

                    <!-- Postal -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_postal") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-mailbox-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.postalCode"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_postal_code_ph')"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Short address -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("address_short") }}
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-home-map-marker text-[18px]"></i>
                      </span>
                      <input
                        v-model="form.address.shortAddress"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                        :placeholder="t('address_short_address_ph')"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <!-- ✅ Bank Section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-bank-outline text-secondary"></i>
                  <span>{{ t("consultant_section_bank") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Holder -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_holder") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.holder"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('bank_owner')"
                        />
                      </div>
                    </div>

                    <!-- IBAN -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_iban") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-credit-card-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.bank.iban"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('iban_ph')"
                        />
                      </div>
                    </div>

                    <!-- Bank Name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-bank text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('bank_name')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Actions -->
              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="saving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60"
                  >
                    <i v-if="saving" class="mdi mdi-loading mdi-spin me-2"></i>
                    {{
                      saving ? t("account_btn_saving") : t("account_btn_save")
                    }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("account_required_fields_note") }}
                </p>
              </footer>
            </form>

            <!-- SECURITY -->
            <form v-else class="space-y-6" @submit.prevent="savePassword">
              <div class="p-5">
                <div class="text-sm text-slate-700 dark:text-slate-200">
                  {{ t("security_section_title") }}
                </div>

                <div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_current_password") }}
                    </label>
                    <input
                      v-model="passwordForm.current_password"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_current_password_placeholder')"
                    />
                  </div>
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_new_password") }}
                    </label>
                    <input
                      v-model="passwordForm.password"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_new_password_placeholder')"
                    />
                  </div>
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_confirm_password") }}
                    </label>
                    <input
                      v-model="passwordForm.password_confirmation"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_confirm_password_placeholder')"
                    />
                  </div>
                </div>

                <div class="mt-4">
                  <button
                    type="submit"
                    :disabled="passwordSaving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60"
                  >
                    <i
                      v-if="passwordSaving"
                      class="mdi mdi-loading mdi-spin me-2"
                    ></i>
                    {{
                      passwordSaving
                        ? t("security_btn_saving")
                        : t("security_btn_save")
                    }}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>

        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import api from "@/services/api";
import { translateCookieToastMessage } from "@/utils/cookieErrors";

export default {
  name: "AccountSettingsBase",
  components: { AdminSidebar, AdminTopbar, Toast },

  props: {
    userType: {
      type: String,
      required: true, // "client" | "consultant" | "office"
    },
  },

  data() {
    const initial = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();

    return {
      messages,
      lang: initial.startsWith("ar") ? "ar" : "en",

      isSidebarCollapsed: false,
      isSidebarOpenMobile: false,
      sidebarSearch: "",

      saving: false,
      loading: true,
      activeTab: "account",

      passwordSaving: false,
      passwordForm: {
        current_password: "",
        password: "",
        password_confirmation: "",
      },

      toast: { show: false, type: "success", message: "" },

      errors: { consultant: {}, address: {}, bank: {} },

      user: null,
      hydratedOnce: false,

      form: {
        consultant: {
          name: "",
          office_name: "",
          specialization: "",
          license_number: "",
          qualification: "",
          consultant_type: "",
          logo_url: "",
          image_base64: "",
          image_meta: { name: "", type: "", size: 0 },

          attachments: [],
          existing_attachments: [],
          removed_existing_attachments: [],
        },

        address: {
          city: "",
          district: "",
          street: "",
          building: "",
          additional: "",
          postalCode: "",
          shortAddress: "",
        },

        bank: { holder: "", iban: "", name: "" },
      },

      // translation listeners (same as your working view)
      _langObserver: null,
      _langHandler: null,
    };
  },

  computed: {
    isOffice() {
      return this.userType === "office";
    },

    isConsultant() {
      return this.userType === "consultant";
    },

    pageTitle() {
      if (this.isOffice) return this.t("office_settings_title");
      if (this.isConsultant) return this.t("consultant_settings_title");
      return this.t("account_settings_title");
    },

    pageSubtitle() {
      if (this.isOffice) return this.t("office_settings_subtitle");
      if (this.isConsultant) return this.t("consultant_settings_subtitle");
      return this.t("account_settings_subtitle");
    },

    // ✅ robust consultant id
    consultantId() {
      const p = this.$route?.params || {};

      const raw =
        p.id ??
        p.consultantId ??
        p.consultant_id ??
        p.consultantID ??
        p.profileId ??
        null;

      if (raw != null && String(raw).trim() !== "") return Number(raw);

      const path = this.$route?.path || "";
      const m = path.match(/\/consultants\/(\d+)/);
      if (m && m[1]) return Number(m[1]);

      const cookieId = this.getConsultantIdFromAuthCookie();
      if (cookieId) return Number(cookieId);

      return 0;
    },

    approveShowEndpoints() {
      const id =
        this.consultantId || Number(this.getConsultantIdFromAuthCookie() || 0);
      if (!id) return [];
      return [`/consultants/${id}`];
    },
  },

  async mounted() {
    // ✅ same translation setup as the working client view
    this.lang = this.readLangFromSources();

    this._langHandler = (e) => {
      const next = this.normalizeLang(e.detail || "ar");
      if (next !== this.lang) this.lang = next;
    };
    window.addEventListener("app:lang", this._langHandler, { passive: true });

    this._langObserver = new MutationObserver(() => {
      const next = this.readLangFromSources();
      if (next !== this.lang) this.lang = next;
    });

    this._langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });

    await this.initUserFromApi();
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler)
      window.removeEventListener("app:lang", this._langHandler);
  },

  watch: {
    lang: {
      immediate: true,
      handler(next) {
        const safe = next === "en" ? "en" : "ar";
        document.documentElement.lang = safe;
        document.documentElement.dir = safe === "ar" ? "rtl" : "ltr";
        localStorage.setItem("app_lang", safe);
      },
    },
  },

  methods: {
    // ✅ same normalize logic used everywhere
    normalizeLang(raw) {
      return String(raw || "").toLowerCase().startsWith("en") ? "en" : "ar";
    },

    // ✅ read from html OR localStorage
    readLangFromSources() {
      return this.normalizeLang(
        document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
      );
    },

    // ✅ safe translator (prevents key showing when message exists)
    t(key) {
      return (this.messages?.[this.lang] && this.messages[this.lang][key]) || key;
    },

    toggleSidebar() {
      if (window.innerWidth < 768)
        this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
      else this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },

    showToast(message, type = "success") {
      this.toast.message = translateCookieToastMessage(message, this.t.bind(this));
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => (this.toast.show = false), 2500);
    },

    async fetchFirstOk(endpoints) {
      let lastErr = null;

      for (const url of endpoints) {
        try {
          const res = await api.get(url, {
            headers: { Accept: "application/json" },
          });
          return res;
        } catch (e) {
          lastErr = e;
        }
      }

      throw lastErr || new Error("All approve endpoints failed");
    },

    async initUserFromApi() {
      this.loading = true;
      this.hydratedOnce = false;

      try {
        if (this.isOffice || this.userType === "consultant") {
          const res = await this.fetchFirstOk(this.approveShowEndpoints);
          const root = res.data?.data ?? res.data;

          this.user = root;
          this.fillFormFromUser(true);
          return;
        }

        const meRes = await api.get("/clients", {
          headers: { Accept: "application/json" },
        });

        const me = meRes.data?.data ?? meRes.data;
        this.user = me.client ?? me;

        this.fillFormFromUser(true);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          this.t("account_saved_failed");

        this.showToast(msg, "error");
      } finally {
        this.loading = false;
      }
    },

    fillFormFromUser() {
      if (!this.user) return;

      const root = this.user;
      const c = root;

      this.form.consultant.name = c?.name ?? "";
      this.form.consultant.office_name = c?.office_name ?? "";
      this.form.consultant.license_number =
        c?.license_number ?? c?.commercial_register ?? c?.license ?? "";

      this.form.consultant.logo_url = this.absoluteUrl(
        c?.logo ?? c?.logo_url ?? c?.image ?? c?.avatar ?? ""
      );

      const addr = root?.address ?? c?.address ?? null;
      this.form.address.city = addr?.city ?? "";
      this.form.address.district = addr?.district ?? "";
      this.form.address.street = addr?.street ?? "";
      this.form.address.building = addr?.building_number ?? "";
      this.form.address.additional = addr?.additional_number ?? "";
      this.form.address.postalCode = addr?.postal_code ?? "";
      this.form.address.shortAddress = addr?.short_address ?? "";

      const bank = root?.bank ?? c?.bank ?? root?.bank_account ?? null;
      this.form.bank.holder = bank?.account_holder_name ?? "";
      this.form.bank.iban = bank?.account_number ?? bank?.iban ?? "";
      this.form.bank.name = bank?.bank_name ?? "";

      this.hydratedOnce = true;
    },

    async saveSettings() {
      if (this.loading) return;
      this.saving = true;

      try {
        const consultantBlock = {
          name: this.form.consultant.name,
          license_number: this.form.consultant.license_number,

          office_name:
            this.form.consultant.office_name || this.form.consultant.name,

          ...(this.form.consultant.image_base64
            ? {
                image: {
                  base64: this.form.consultant.image_base64,
                  name: this.form.consultant.image_meta.name || "image.png",
                  type: this.form.consultant.image_meta.type || "image/png",
                  size: this.form.consultant.image_meta.size || 0,
                },
              }
            : {}),
        };

        const payload = {
          consultant: consultantBlock,
          address: {
            city: this.form.address.city,
            district: this.form.address.district,
            street: this.form.address.street,
            building_number: this.form.address.building,
            additional_number: this.form.address.additional,
            postal_code: this.form.address.postalCode,
            short_address: this.form.address.shortAddress,
          },
          bank: {
            account_holder_name: this.form.bank.holder,
            account_number: this.form.bank.iban,
            bank_name: this.form.bank.name,
          },
        };

        await api.post("/consultants/update-consultant-office-profile", payload, {
          headers: { Accept: "application/json" },
        });

        this.form.consultant.image_base64 = "";
        this.form.consultant.attachments = [];

        await this.initUserFromApi();

        this.showToast(this.t("account_saved_success"), "success");
      } catch (err) {
        const res = err.response?.data;
        if (res?.errors) {
          const msg = Object.values(res.errors).flat().filter(Boolean).join("\n");
          this.showToast(msg, "error");
        } else {
          this.showToast(res?.message || this.t("account_saved_failed"), "error");
        }
      } finally {
        this.saving = false;
      }
    },

    async savePassword() {
      if (this.passwordSaving || this.loading) return;
      this.passwordSaving = true;

      try {
        const bodyData = {
          old_password: this.passwordForm.current_password,
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.password_confirmation,
        };

        const { data: res } = await api.patch("/users/updatePassword", bodyData, {
          headers: { Accept: "application/json" },
        });

        this.showToast(
          res?.message || this.t("security_password_success"),
          "success"
        );

        this.passwordForm.current_password = "";
        this.passwordForm.password = "";
        this.passwordForm.password_confirmation = "";
      } catch (err) {
        const res = err.response?.data;
        if (res?.errors) {
          const msg = Object.values(res.errors).flat().filter(Boolean).join("\n");
          this.showToast(msg, "error");
        } else {
          this.showToast(
            res?.message || this.t("security_password_failed"),
            "error"
          );
        }
      } finally {
        this.passwordSaving = false;
      }
    },

    async onImageChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      this.form.consultant.image_base64 = String(dataUrl).split(",")[1] || "";
      this.form.consultant.image_meta = {
        name: file.name,
        type: file.type,
        size: file.size,
      };

      this.form.consultant.logo_url = dataUrl;
    },

    absoluteUrl(u) {
      if (!u) return "";
      if (u.startsWith("http://") || u.startsWith("https://")) return u;

      const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
      const path = u.startsWith("/") ? u : `/${u}`;
      return base ? `${base}${path}` : u;
    },

    getConsultantIdFromAuthCookie() {
      try {
        const raw = this.getCookie("auth_user");
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        return (
          parsed?.consultant?.id ||
          parsed?.user?.consultant_id ||
          parsed?.id ||
          null
        );
      } catch (e) {
        return null;
      }
    },

    getCookie(name) {
      const match = document.cookie.match(
        new RegExp(
          "(?:^|; )" +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
            "=([^;]*)"
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    },
  },
};
</script>